import React, { useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { useFavoriteProductsStore } from "@src/state/favoriteProductsState";
import styles from "./FavoriteHeartButton.module.scss";

const ADDED_PILL_DURATION_MS = 2000;

interface FavoriteHeartButtonProps {
  productId: string;
  variant: "tile" | "pdp";
  className?: string;
}

export default function FavoriteHeartButton({
  productId,
  variant,
  className,
}: FavoriteHeartButtonProps) {
  const { isFavorited, addFavorite, removeFavorite } =
    useFavoriteProductsStore();
  const favorited = isFavorited(productId);
  const [showAddedPill, setShowAddedPill] = useState(false);
  const pillTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (pillTimeoutRef.current) {
        clearTimeout(pillTimeoutRef.current);
      }
    };
  }, []);

  // Tile variant: display-only red heart, only renders when favorited
  if (variant === "tile") {
    if (!favorited) return null;
    return (
      <span
        className={classnames(styles.tileHeart, className)}
        data-testid={`favorite-tile-heart-${productId}`}
        aria-label="Favorited"
      >
        <i className="fa-solid fa-heart" />
      </span>
    );
  }

  // PDP variant: bordered toggle button with "Added to Favorites" pill
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (favorited) {
      removeFavorite(productId);
      setShowAddedPill(false);
      if (pillTimeoutRef.current) {
        clearTimeout(pillTimeoutRef.current);
      }
    } else {
      addFavorite(productId);
      setShowAddedPill(true);
      if (pillTimeoutRef.current) {
        clearTimeout(pillTimeoutRef.current);
      }
      pillTimeoutRef.current = setTimeout(() => {
        setShowAddedPill(false);
      }, ADDED_PILL_DURATION_MS);
    }
  };

  return (
    <div className={classnames(styles.pdpWrapper, className)}>
      <div
        className={classnames(styles.addedPill, {
          [styles.addedPillVisible]: showAddedPill,
        })}
        aria-hidden={!showAddedPill}
        data-testid={`favorite-added-pill-${productId}`}
      >
        Added to Favorites
      </div>
      <button
        type="button"
        className={classnames(
          styles.pdpButton,
          favorited ? styles.pdpButtonSelected : styles.pdpButtonDeselected,
        )}
        onClick={handleClick}
        aria-label={favorited ? "Remove from favorites" : "Add to favorites"}
        aria-pressed={favorited}
        data-testid={`favorite-pdp-button-${productId}`}
      >
        <i className="fa-solid fa-heart" />
      </button>
    </div>
  );
}
